import { z } from 'zod';
import type { MoneyJson } from '../money/money';
import { moneySchema } from './common.schemas';

/**
 * Garage quotes on a work order (gitaumoses4/chukua#167). The garage is free text until the garages
 * directory (gitaumoses4/chukua#173) merges; a `garageId` column is then additive.
 */

/** `POST /work-orders/:id/quotes` — `uploadId` is a confirmed upload for entity type `work_order_quote` and this work order's id. */
export const createWorkOrderQuoteInputSchema = z
  .object({
    garageName: z.string().trim().min(1).max(200),
    amount: moneySchema.refine((money) => money.amountMinor > 0, { message: 'A quote must be more than zero', path: ['amountMinor'] }),
    note: z.string().trim().min(1).max(1000).optional(),
    uploadId: z.uuid().optional(),
  })
  .strict();
export type CreateWorkOrderQuoteInput = z.infer<typeof createWorkOrderQuoteInputSchema>;

export const withdrawWorkOrderQuoteInputSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();
export type WithdrawWorkOrderQuoteInput = z.infer<typeof withdrawWorkOrderQuoteInputSchema>;

/** The reason is required when the chosen quote is not the cheapest one; the server decides, so it is optional here. */
export const chooseWorkOrderQuoteInputSchema = z.object({ reason: z.string().trim().min(1).max(500).optional() }).strict();
export type ChooseWorkOrderQuoteInput = z.infer<typeof chooseWorkOrderQuoteInputSchema>;

export interface WorkOrderQuoteView {
  id: string;
  workOrderId: string;
  garageName: string;
  amount: MoneyJson;
  note: string | null;
  uploadId: string | null;
  recordedById: string;
  createdAt: string;
  isChosen: boolean;
  choiceReason: string | null;
  withdrawnAt: string | null;
  withdrawnById: string | null;
  withdrawalReason: string | null;
}

/** Why Approve is or is not available on the quote side: the same answer the approval itself acts on. */
export interface WorkOrderApprovalReadiness {
  quotesRequired: boolean;
  /** The estimate above which quotes are needed, in the order's currency; null when that currency has no threshold (quotes always required). */
  quoteThreshold: MoneyJson | null;
  isMet: boolean;
  reasons: string[];
}
